import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { useLeaveWorkspaceScopedRoute } from '@/hooks/useLeaveWorkspaceScopedRoute'

/**
 * Query-key roots that describe the ACCOUNT, not a workspace.
 *
 * These survive a workspace switch. Resetting them would blank the auth /
 * entitlement state that route guards read, causing a redirect flicker
 * mid-navigation — and they'd refetch to exactly the same values anyway.
 */
const ACCOUNT_SCOPED_KEYS = new Set([
  'userOrgId',
  'profile',
  'preferences',
  'settings',
  'entitlements',
  'wallet',
  'workspaces',
  'workspaces-tree',
  'workspace',
  'workspace-members',
  'workspace-resources',
])

/**
 * localStorage keys holding a project/application from the workspace we are
 * leaving. None of them survive a switch.
 *
 * - `qyngent_active_project` is useActiveProject's legacy un-namespaced key
 *   (it is per-workspace since 2026-09-05, but an older value would still be
 *   picked up by the no-workspace fallback path).
 * - `qyngent:lastProject` / `qyngent:lastApplication` are the BuildPage
 *   session (useProjectSession). It validates them with GET /api/projects/{id},
 *   which is workspace-agnostic — so a project from the old workspace would
 *   validate fine and stay selected on /build after the switch.
 */
export const CROSS_WORKSPACE_KEYS = [
  'qyngent_active_project',
  'qyngent:lastProject',
  'qyngent:lastApplication',
]

/**
 * Switch the active workspace and purge everything scoped to the old one.
 *
 * The store write alone is not enough: every TanStack entry would keep
 * serving the previous workspace's rows until its staleTime expired, so the
 * switch looked like a no-op and briefly rendered workspace A's data under a
 * header that already said B.
 *
 * `removeQueries` (rather than `invalidateQueries`) is deliberate — we want a
 * clean loading state, not a flash of the other workspace's data.
 *
 * Pass `orgId` (every `Workspace` carries `org_id`) when the target workspace
 * may live in a different org. A workspace belongs to exactly one org and the
 * backend derives `request.state.org_id` from the selected workspace, so the
 * two stored selections must move together — otherwise apiFetch keeps
 * stamping the org the user just left, and every account-level surface
 * (billing, team, the org name) reads the wrong tenant. When the org really
 * changes, the account-scoped caches are no longer account-scoped: they are
 * *other-tenant* data, so they go too.
 */
export function useSwitchWorkspace() {
  const queryClient = useQueryClient()
  const leaveWorkspaceScopedRoute = useLeaveWorkspaceScopedRoute()
  const setActiveWorkspace = useWorkspaceStore((s) => s.setActiveWorkspace)
  const setActiveOrg = useWorkspaceStore((s) => s.setActiveOrg)

  return useCallback(
    (workspaceId: string | null, orgId?: string | null) => {
      if (useWorkspaceStore.getState().activeWorkspaceId === workspaceId) return

      const orgChanged =
        !!orgId && useWorkspaceStore.getState().activeOrgId !== orgId

      setActiveWorkspace(workspaceId)
      if (orgId) setActiveOrg(orgId)

      queryClient.removeQueries({
        predicate: (query) => {
          if (orgChanged) return true
          const root = query.queryKey[0]
          return typeof root !== 'string' || !ACCOUNT_SCOPED_KEYS.has(root)
        },
      })

      try {
        CROSS_WORKSPACE_KEYS.forEach((k) => localStorage.removeItem(k))
      } catch {
        // Private mode — nothing to clean up.
      }

      // Leave any route whose PATH names workspace-scoped state.
      //
      // Clearing the localStorage candidates above is not enough on its own:
      // /build/:projectId/:applicationId encodes a project and an application
      // that belong to the workspace being LEFT, and the session-restore
      // waterfall's first rule is "URL deep-link wins" — so the cleared value
      // was immediately reseeded from the address bar, and the switch looked
      // like it had done nothing.
      //
      // Dropping the deep link here removes the race rather than policing it.
      // Guarding the restore instead meant the page resolved to the previous
      // workspace's project and then corrected itself, which made correctness
      // depend on validation timing. The restore-side workspace check stays as
      // defence-in-depth, because navigation cannot help a link that was pasted
      // or bookmarked from another workspace.
      //
      // `replace` so the switch does not leave a Back button that returns the
      // user to a project their current workspace does not contain.
      leaveWorkspaceScopedRoute()
    },
    [queryClient, setActiveWorkspace, setActiveOrg, leaveWorkspaceScopedRoute],
  )
}
